import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import { useMemo } from "react";
import useWholeFirmRoster from "work/work-agreements/teams/client-team/_views/use-whole-firm-roster";
import useTeamIndividuals from "work/work-agreements/teams/leader-handover/_views/use-team-individuals";
import type {
  SuccessorCandidate,
  SuccessorCredentials,
} from "work/work-agreements/teams/leader-handover/_views/successor-table";

type SuccessorCandidatesArgs = {
  outgoingLeader: EntityMember;
  /** Everybody on the team now. */
  teamMembers: readonly EntityMember[];
  /** People who may not take the team over: those on the other side of the work. */
  excludedUserIds: readonly string[];
  /**
   * List everybody at the outgoing leader's firm, not only the team. False for a client leader
   * proposing a new vendor leader, who cannot read the vendor's firm.
   */
  listWholeFirm: boolean;
  /**
   * A colleague who is not on the team joins it by leading it and must hold professional
   * credentials: true for a vendor team (#5800).
   */
  joiningRequiresCredentials: boolean;
};

export type SuccessorCandidates = {
  candidates: SuccessorCandidate[];
  isLoading: boolean;
  /** The firm could not be listed -- the reader may not read its roster -- so only the team is offered. */
  firmIsUnlisted: boolean;
  /** Read the firm again, as a colleague who has just added their credentials asks. */
  refresh: () => void;
};

/**
 * Everybody who can take a team over from `outgoingLeader` (#5836): their colleagues at the same
 * firm, on the team or not, except the leader and anybody on the other side of the work. When the
 * firm's roster cannot be read, the team members from the firm are still offered.
 *
 * The whole roster is read, not a page of it, because the table filters on signing authority and
 * the members endpoint cannot.
 */
export default function useSuccessorCandidates(args: Readonly<SuccessorCandidatesArgs>): SuccessorCandidates {
  const { outgoingLeader, teamMembers, excludedUserIds, listWholeFirm, joiningRequiresCredentials } = args;
  // Fresh, because the dialog refuses an offer on what the roster says of credentials (#6095).
  const roster = useWholeFirmRoster(outgoingLeader.legalEntityId, { skip: !listWholeFirm, fresh: true });
  const colleaguesOnTheTeam = teamMembers.filter((member) => member.legalEntityId.isEqualTo(outgoingLeader.legalEntityId));
  const teamIndividuals = useTeamIndividuals(colleaguesOnTheTeam);
  const rosterJSON = roster.data;
  const firmMembers = useMemo(() => (rosterJSON ? EntityMemberSummary.fromJSONArray(rosterJSON) : []), [rosterJSON]);

  const onTheTeam = new Set(colleaguesOnTheTeam.map((member) => member.userId.value));
  const excluded = new Set([outgoingLeader.userId.value, ...excludedUserIds]);
  const byUserId = new Map<string, SuccessorCandidate>();
  const add = (candidate: Omit<SuccessorCandidate, "onTheTeam">) => {
    const userId = candidate.member.userId.value;
    if (excluded.has(userId) || byUserId.has(userId)) return;
    byUserId.set(userId, { ...candidate, onTheTeam: onTheTeam.has(userId) });
  };
  for (const summary of firmMembers) {
    const joinsTheTeam = !onTheTeam.has(summary.userId.value);
    let credentials: SuccessorCredentials = "notNeeded";
    if (joiningRequiresCredentials && joinsTheTeam) credentials = summary.hasCredentials ? "onTheirProfile" : "missing";
    add({
      member: new EntityMember({ legalEntityId: outgoingLeader.legalEntityId, userId: summary.userId }),
      individual: summary.asIndividual(),
      name: summary.fullName,
      email: summary.email,
      hasSigningAuthority: summary.hasSigningAuthority,
      credentials,
    });
  }
  for (const member of colleaguesOnTheTeam) {
    const individual = teamIndividuals.get(member.userId.value);
    add({
      member: new EntityMember({ legalEntityId: outgoingLeader.legalEntityId, userId: member.userId }),
      individual,
      name: individual?.fullName || "A team member",
      email: individual?.email?.value,
      // Not on the roster, so the profile is all there is to go on. A profile the reader could not
      // read says nothing about signing authority, and must not read as lacking it (#6095).
      hasSigningAuthority: individual?.hasSigningAuthority,
      // Already on the team, so leading it asks nothing of their credentials.
      credentials: "notNeeded",
    });
  }

  return {
    candidates: [...byUserId.values()],
    isLoading: roster.isLoading,
    firmIsUnlisted: listWholeFirm && roster.error !== undefined,
    refresh: roster.refetch,
  };
}
